/**
 * Coach Notes Library panel (post-Sprint 12).
 *
 * Coach-side reference library inline in the Coaching Plan panel.
 * 14 notes (7 dimensions × 2 bands), with profile-relevance
 * highlighting and per-element copy-to-clipboard.
 *
 * NOT persisted per coachee. NEVER rendered in the PDF. NEVER seen
 * by the client. Editorial-integrity guard in
 * `backend/tests/test_coach_notes.py` enforces the PDF-side prohibition
 * mechanically.
 *
 * Panel placement: BELOW the three plan textareas in `CoachingPlanPanel`
 * — coach composes the plan first, library is the look-up if stuck
 * (per user preference at build time).
 */
import React from "react";
import { api, formatApiError } from "../lib/api";

const STORAGE_KEY = "rt.coachPref.coachNotes.expanded";

function loadExpanded() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "true";  // default false (collapsed)
  } catch {
    return false;
  }
}

function saveExpanded(v) {
  try {
    localStorage.setItem(STORAGE_KEY, String(v));
  } catch {
    /* ignore quota / disabled */
  }
}

/**
 * Copy-to-clipboard with a fallback for browsers that don't support
 * the async Clipboard API (older Safari, restricted contexts). The
 * fallback uses a hidden textarea + document.execCommand('copy').
 * Returns a boolean — true on success, false on failure.
 */
async function copyText(text) {
  // Modern path
  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy */
    }
  }
  // Legacy execCommand fallback
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

function ToastViewer({ message, visible }) {
  if (!visible) return null;
  return (
    <div
      data-testid="coach-notes-copy-toast"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        padding: "10px 16px",
        background: "#1A1A1A",
        color: "white",
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontSize: "13px",
        letterSpacing: "0.04em",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        zIndex: 9999,
      }}
    >
      {message}
    </div>
  );
}

function CopyIconButton({ onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      style={{
        background: "transparent",
        border: "1px solid #E0E0E0",
        padding: "4px 8px",
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontSize: "10px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#6F7280",
        cursor: "pointer",
      }}
    >
      Copy
    </button>
  );
}

function NoteCard({ note, isHighlighted, onCopy }) {
  // Color discipline matches Sprint 12 PDF: green = strength/leverage,
  // amber = gap/practice. 30% alpha on the border to match the
  // cross-dimensional sections.
  const borderColor = note.band === "HIGH"
    ? "rgba(5, 150, 105, 0.5)"
    : "rgba(217, 119, 6, 0.5)";
  const bandLabelColor = note.band === "HIGH" ? "#059669" : "#D97706";
  const cellKey = `${note.dimension}-${note.band}`;

  return (
    <article
      data-testid={`coach-note-${cellKey}`}
      data-band={note.band}
      data-highlighted={isHighlighted ? "true" : "false"}
      style={{
        padding: "14px 16px",
        marginBottom: "10px",
        background: "white",
        borderLeft: `3px solid ${borderColor}`,
        border: "1px solid #F0F0F0",
        borderLeftWidth: "3px",
        borderLeftColor: borderColor,
      }}
    >
      <header style={{ marginBottom: "10px" }}>
        {isHighlighted && (
          <div
            data-testid={`coach-note-relevant-tag-${cellKey}`}
            className="eyebrow"
            style={{
              color: bandLabelColor,
              fontSize: "9px",
              marginBottom: "4px",
            }}
          >
            Relevant to this coachee
          </div>
        )}
        <div className="flex items-baseline justify-between gap-2">
          <h4
            style={{
              fontFamily: "Oswald",
              fontWeight: 700,
              fontSize: "14px",
              color: "#1A1A1A",
              margin: 0,
            }}
          >
            {note.dimension}
          </h4>
          <span
            className="text-xs uppercase tracking-widest"
            style={{ color: bandLabelColor, fontFamily: "Oswald", fontSize: "10px" }}
          >
            {note.band}
          </span>
        </div>
      </header>

      <Section
        eyebrow="Pattern"
        body={note.pattern}
        cellKey={cellKey}
        field="pattern"
        onCopy={onCopy}
        bodyStyle={{ color: "#444" }}
      />
      <Section
        eyebrow={note.action_label}
        body={note.action_text}
        cellKey={cellKey}
        field="action"
        onCopy={onCopy}
        bodyStyle={{ color: "#1A1A1A", fontWeight: 500 }}
      />
      <Section
        eyebrow="Coaching question"
        body={note.coaching_question}
        cellKey={cellKey}
        field="question"
        onCopy={onCopy}
        bodyStyle={{ color: "#2F6B7C", fontStyle: "italic" }}
      />
    </article>
  );
}

function Section({ eyebrow, body, cellKey, field, onCopy, bodyStyle }) {
  return (
    <div style={{ marginTop: "10px" }} data-testid={`coach-note-section-${cellKey}-${field}`}>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="eyebrow" style={{ color: "#7F7F7F", fontSize: "9px" }}>
          {eyebrow}
        </div>
        <CopyIconButton
          onClick={() => onCopy(body, eyebrow)}
          label={`Copy ${eyebrow.toLowerCase()}`}
          testId={`coach-note-copy-${cellKey}-${field}`}
        />
      </div>
      <p
        style={{
          fontFamily: "Georgia, serif",
          fontSize: "13px",
          lineHeight: 1.55,
          margin: 0,
          ...bodyStyle,
        }}
      >
        {body}
      </p>
    </div>
  );
}

export default function CoachNotesPanel({ respondentId, isCompleted }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [expanded, setExpanded] = React.useState(() => {
    // Spec Part 4.1 + user preference (build time): force-collapsed
    // pre-completion regardless of stored coach preference. No
    // highlighting = no profile-specific value yet, so don't surface
    // the panel until the survey is done.
    if (!isCompleted) return false;
    return loadExpanded();
  });
  const [toastMessage, setToastMessage] = React.useState("");
  const toastTimerRef = React.useRef(null);

  // Re-evaluate expanded state when isCompleted flips (e.g. survey
  // completes while coach is on the page). Respect stored preference
  // once completion is true.
  React.useEffect(() => {
    if (!isCompleted) {
      setExpanded(false);
    }
  }, [isCompleted]);

  React.useEffect(() => {
    if (!expanded || !respondentId || data) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: body } = await api.get(`/api/respondents/${respondentId}/coach-notes`);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, respondentId, data]);

  const toggle = () => {
    setExpanded((e) => {
      const next = !e;
      // Only persist preference once the panel is meaningful (post-
      // completion). Pre-completion the panel is force-collapsed
      // regardless, so storing 'true' would be silently ignored.
      if (isCompleted) saveExpanded(next);
      return next;
    });
  };

  const onCopy = async (text, label) => {
    const ok = await copyText(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(ok ? `Copied: ${label}` : "Copy failed — select and copy manually");
    toastTimerRef.current = setTimeout(() => setToastMessage(""), 2000);
  };

  const highlightedSet = React.useMemo(() => {
    if (!data?.highlighted_keys) return new Set();
    return new Set(data.highlighted_keys.map((k) => `${k.dimension}-${k.band}`));
  }, [data]);

  const sortedNotes = React.useMemo(() => {
    if (!data?.all_notes) return [];
    // Highlighted notes first (already in canonical dimension order
    // server-side), then the rest in their original order.
    const highlighted = [];
    const rest = [];
    for (const n of data.all_notes) {
      if (highlightedSet.has(`${n.dimension}-${n.band}`)) {
        highlighted.push(n);
      } else {
        rest.push(n);
      }
    }
    return [...highlighted, ...rest];
  }, [data, highlightedSet]);

  if (!respondentId) return null;

  const highlightCount = data?.highlighted_keys?.length || 0;

  return (
    <section data-testid="coach-notes-panel" style={{ marginTop: "16px" }}>
      <button
        type="button"
        onClick={toggle}
        data-testid="coach-notes-toggle"
        aria-expanded={expanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: "white",
          border: "1px solid #E0E0E0",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div>
          <div className="eyebrow" style={{ color: "#2563EB" }}>
            Coach reference
          </div>
          <h4
            style={{
              fontFamily: "Oswald",
              fontWeight: 700,
              fontSize: "16px",
              color: "#1A1A1A",
              margin: "4px 0 0 0",
            }}
          >
            Coach notes for this profile (14)
          </h4>
          {expanded && highlightCount > 0 && (
            <div
              data-testid="coach-notes-relevance-count"
              style={{
                fontSize: "12px",
                color: "#7F7F7F",
                marginTop: "4px",
              }}
            >
              {highlightCount} highlighted as relevant to this coachee
            </div>
          )}
        </div>
        <div
          className="text-xs uppercase tracking-widest"
          style={{ color: "#7F7F7F", fontFamily: "Oswald" }}
        >
          {expanded ? "Hide" : "Show"}
        </div>
      </button>

      {expanded && (
        <div
          style={{
            marginTop: "10px",
            padding: "14px",
            background: "#FBFAF7",
            border: "1px solid #E0E0E0",
          }}
          data-testid="coach-notes-content"
        >
          {loading && (
            <div className="text-sm text-gray-500">Loading coach notes…</div>
          )}
          {error && (
            <div
              data-testid="coach-notes-error"
              className="text-sm"
              style={{ color: "#AD393E" }}
            >
              {error}
            </div>
          )}
          {!loading && !error && sortedNotes.length > 0 && (
            <div>
              {sortedNotes.map((n) => (
                <NoteCard
                  key={`${n.dimension}-${n.band}`}
                  note={n}
                  isHighlighted={highlightedSet.has(`${n.dimension}-${n.band}`)}
                  onCopy={onCopy}
                />
              ))}
            </div>
          )}
          {!loading && !error && !isCompleted && (
            <p
              style={{
                fontSize: "12px",
                color: "#7F7F7F",
                fontStyle: "italic",
                marginBottom: "12px",
              }}
            >
              The coachee hasn't completed the survey yet — all 14 notes are
              browseable but none are highlighted as profile-relevant.
            </p>
          )}
        </div>
      )}

      <ToastViewer message={toastMessage} visible={Boolean(toastMessage)} />
    </section>
  );
}
